import { ChoiceButton, WizardInput, WizardOk, WizardStep } from "@/components/wizard/wizard-step";

import type { Ficha } from "../../-utils/ficha";
import { BOOLEAN_NO, BOOLEAN_YES, type FieldKind } from "../../-utils/fields";

import { DateStep } from "./date-step";
import { PhotoStep } from "./photo-step";

const TEXTUAL_KINDS = new Set<FieldKind>(["text", "tel", "email", "cpf", "cep"]);

function isTextual(kind: FieldKind) {
	return TEXTUAL_KINDS.has(kind);
}

function SkipButton({ onSkip }: { onSkip: () => void }) {
	return (
		<button
			type="button"
			onClick={onSkip}
			className="-ml-2 flex min-h-11 items-center self-start rounded-xl px-2 text-lg text-muted-foreground underline hover:text-foreground"
		>
			Pular
		</button>
	);
}

function inputMode(kind: FieldKind) {
	if (kind === "cpf" || kind === "cep") {
		return "numeric";
	}

	if (kind === "tel") {
		return "tel";
	}

	if (kind === "email") {
		return "email";
	}

	return "text";
}

export function FormStep({ ficha }: { ficha: Ficha }) {
	const { step, fields, field, value, error, cep, photoPreview, inputRef, actions } = ficha;

	return (
		<WizardStep
			step={step}
			total={fields.length}
			label={field.label}
			hint={field.hint}
			error={error}
			onBack={actions.back}
			footer={
				<>
					{isTextual(field.kind) && (
						<div className="mt-8 flex flex-wrap items-center gap-4">
							<WizardOk onClick={actions.advance} />

							{!!field.optional && <SkipButton onSkip={actions.skip} />}
						</div>
					)}

					{field.kind === "date" && (
						<div className="mt-8">
							<WizardOk onClick={actions.advance} />
						</div>
					)}

					{(field.kind === "choice" || field.kind === "boolean") && !!field.optional && (
						<div className="mt-8">
							<SkipButton onSkip={actions.skip} />
						</div>
					)}
				</>
			}
		>
			{field.kind === "choice" && (
				<div className="flex flex-col gap-3">
					{field.options?.map((option) => (
						<ChoiceButton
							key={option}
							active={value === option}
							onClick={() => actions.choose(option)}
						>
							{option}
						</ChoiceButton>
					))}
				</div>
			)}

			{field.kind === "boolean" && (
				<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
					<ChoiceButton
						active={value === BOOLEAN_YES}
						onClick={() => actions.choose(BOOLEAN_YES)}
						className="text-center"
					>
						Sim
					</ChoiceButton>

					<ChoiceButton
						active={value === BOOLEAN_NO}
						onClick={() => actions.choose(BOOLEAN_NO)}
						className="text-center"
					>
						Não
					</ChoiceButton>
				</div>
			)}

			{field.kind === "photo" && (
				<PhotoStep
					preview={photoPreview}
					onCapture={actions.pickPhoto}
					onPick={actions.pickPhoto}
					onClear={actions.clearPhoto}
					onConfirm={actions.advance}
				/>
			)}

			{field.kind === "date" && <DateStep value={value} onChange={actions.set} />}

			{isTextual(field.kind) && (
				<>
					<WizardInput
						inputRef={inputRef}
						value={value}
						inputMode={inputMode(field.kind)}
						onChange={actions.set}
						onEnter={actions.advance}
					/>

					{field.kind === "cep" && !!cep && (
						<p className="mt-3 text-lg text-whatsapp">
							{cep.street}, {cep.district} - {cep.city}/{cep.uf}
						</p>
					)}
				</>
			)}
		</WizardStep>
	);
}
